"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { CheckIcon, XIcon } from "lucide-react";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AiModelAccess } from "@/lib/ai/model-access";

const CONTACT_URL = "https://amethy.st/contact/";

type AiModelUpgradeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  aiModelAccess: Pick<AiModelAccess, "currentOption">;
};

export function AiModelUpgradeDialog({
  open,
  onOpenChange,
  aiModelAccess,
}: AiModelUpgradeDialogProps) {
  const { i18n } = useLingui();
  const { currentOption } = aiModelAccess;
  const currentPlanLabel =
    currentOption === "AI_MAX"
      ? i18n._(msg`AI MAX`)
      : currentOption === "AI_PRO"
        ? i18n._(msg`AI PRO`)
        : i18n._(msg`AI Standard`);
  const currentPlanPriceLabel =
    currentOption === "AI_MAX"
      ? i18n._(msg`Price on request`)
      : currentOption === "AI_PRO"
        ? i18n._(msg`$300 / month (tax included)`)
        : i18n._(msg`$100 / month (tax included)`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[780px]">
        <DialogHeader className="mb-2">
          <DialogTitle>
            <Trans>Unlock advanced AI models</Trans>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="relative flex h-full flex-col rounded-lg border border-border bg-muted/20 p-4">
              <Badge
                variant="secondary"
                className="-top-2.5 left-3 absolute text-[10px]"
              >
                <Trans>Current plan</Trans>
              </Badge>
              <div className="text-lg font-semibold">{currentPlanLabel}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {currentPlanPriceLabel}
              </div>
              <ul className="mt-4 space-y-2 text-xs text-muted-foreground">
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Claude Haiku 5.5</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Claude Sonnet 5.5</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Gemini 3.8 Flash</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <XIcon className="h-3 w-3 text-muted-foreground" />
                  <Trans>Claude Opus 5.5</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <XIcon className="h-3 w-3 text-muted-foreground" />
                  <Trans>Gemini 3.1 Pro Preview</Trans>
                </li>
              </ul>
            </div>
            <div className="relative flex h-full flex-col rounded-lg border-2 border-primary bg-primary/10 p-4 shadow-lg shadow-primary/20">
              <Badge className="-top-2.5 left-3 absolute text-[10px]">
                <Trans>Recommended</Trans>
              </Badge>
              <div className="text-lg font-semibold">
                <Trans>AI PRO</Trans>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                <Trans>$300 / month (tax included)</Trans>
              </div>
              <div className="mt-1 text-sm">
                <Trans>
                  Fixed monthly plan with access to advanced models like Opus
                  and Gemini Pro.
                </Trans>
              </div>
              <ul className="mt-4 space-y-2 text-xs text-muted-foreground">
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Claude Haiku 5.5</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Claude Sonnet 5.5</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Claude Opus 5.5</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Gemini 3.8 Flash</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Gemini 3.1 Pro Preview</Trans>
                </li>
              </ul>
              <div className="mt-auto pt-4">
                <Button
                  asChild
                  className="w-full bg-primary text-primary-foreground shadow-md shadow-primary/30 hover:bg-primary/90"
                  size="sm"
                >
                  <Link
                    href={CONTACT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Trans>Apply for AI PRO</Trans>
                  </Link>
                </Button>
              </div>
            </div>
            <div className="flex h-full flex-col rounded-lg border p-4">
              <div className="text-lg font-semibold">
                <Trans>AI MAX</Trans>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                <Trans>Price on request</Trans>
              </div>
              <div className="mt-1 text-sm">
                <Trans>
                  Custom plan with tailored quota, support, and operations for
                  enterprise teams.
                </Trans>
              </div>
              <ul className="mt-4 space-y-2 text-xs text-muted-foreground">
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>All models included in AI PRO</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Quota can be customized per contract</Trans>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckIcon className="h-3 w-3 text-emerald-500" />
                  <Trans>Tailored support and operations</Trans>
                </li>
              </ul>
              <Button
                asChild
                className="mt-auto w-full"
                size="sm"
                variant="outline"
              >
                <Link
                  href={CONTACT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Trans>Consult about AI MAX</Trans>
                </Link>
              </Button>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground"
              onClick={() => onOpenChange(false)}
            >
              <Trans>Continue with current plan</Trans>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
